import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { xpApi, participantAuth } from "@/lib/experienceApi";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, BookOpen, Play, Timer } from "lucide-react";

export default function ExperienceTasting() {
  const { wineId } = useParams();
  const nav = useNavigate();
  const [payload, setPayload] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [wine, setWine] = useState(null);

  useEffect(() => {
    if (!participantAuth.getToken()) { nav("/experience"); return; }
    const cached = sessionStorage.getItem("gv_last_qr");
    if (cached) {
      try {
        const p = JSON.parse(cached);
        if (p.wine?.id === wineId) {
          setPayload(p); setWine(p.wine);
        }
      } catch {}
    }
    if (!payload) {
      xpApi.requestTastingToken(wineId).then((p) => {
        setPayload(p); setWine(p.wine);
        sessionStorage.setItem("gv_last_qr", JSON.stringify(p));
      }).catch((e) => { toast.error(e?.response?.data?.detail || "Impossible"); nav("/experience/my"); });
    }
    // eslint-disable-next-line
  }, [wineId, nav]);

  useEffect(() => {
    if (!payload) return;
    const tick = () => {
      const secs = Math.max(0, Math.floor((new Date(payload.expires_at) - new Date()) / 1000));
      setRemaining(secs);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [payload]);

  const markDiscovered = async () => {
    try {
      const res = await xpApi.markDiscovered(wineId);
      toast.success(res.awarded > 0 ? `+${res.awarded} pts !` : "Déjà découvert");
    } catch (e) { toast.error("Impossible"); }
  };

  if (!payload) return <div className="p-6 text-center text-sm text-neutral-500">Génération du QR…</div>;

  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=500x500&margin=8&data=${encodeURIComponent(payload.token)}`;
  const mm = Math.floor(remaining / 60), ss = remaining % 60;

  return (
    <div className="max-w-xl mx-auto px-4 py-6">
      <button onClick={() => nav("/experience/my")} className="inline-flex items-center gap-1 text-sm text-neutral-600 mb-3">
        <ArrowLeft className="w-4 h-4" /> Retour
      </button>
      <div className="text-center mb-4">
        <div className="text-xs uppercase tracking-widest text-[#E5276A] mb-1">{wine?.wine_ref?.type}</div>
        <h1 className="text-2xl font-display">{wine?.wine_ref?.name}</h1>
        <div className="mt-2 inline-flex items-center gap-1 text-sm text-neutral-600">
          <Timer className="w-4 h-4" />
          {mm.toString().padStart(2, "0")}:{ss.toString().padStart(2, "0")}
        </div>
      </div>
      <Card className="mb-4">
        <CardContent className="p-4 flex flex-col items-center">
          <img src={qrUrl} alt="QR de service" data-testid="tasting-qr" className="w-72 h-72 rounded-lg" crossOrigin="anonymous" />
          <p className="text-xs text-neutral-500 text-center mt-3">Présentez ce QR à un serveur pour recevoir votre dose.</p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" onClick={markDiscovered} data-testid="discover-btn">
          <BookOpen className="w-4 h-4 mr-1" /> Découvrir
        </Button>
        <Button className="bg-[#E5276A] hover:bg-[#C01661]" onClick={() => nav(`/experience/quiz/${wineId}`)} data-testid="play-btn">
          <Play className="w-4 h-4 mr-1" /> Jouer
        </Button>
      </div>
      <p className="text-[11px] text-neutral-500 text-center mt-3">
        Le QR expire dans {mm}:{ss.toString().padStart(2, "0")}. Vous pouvez le régénérer à tout moment.
      </p>
    </div>
  );
}
